import { isNil } from 'lodash';
import { formatTitleCase, intComma } from '~domain/helper/formatters';
import { CharacterMaskRegex } from '~domain/helper/validation-regex';
import { TextInputMask } from '~ui-kit/components/text-input';

// Create postal code mask. Format is "A1A 2A2"
export const createPostalCodeMask = () => [
  CharacterMaskRegex.LETTER_REGEX,
  CharacterMaskRegex.DIGIT_REGEX,
  CharacterMaskRegex.LETTER_REGEX,
  ' ',
  CharacterMaskRegex.DIGIT_REGEX,
  CharacterMaskRegex.LETTER_REGEX,
  CharacterMaskRegex.DIGIT_REGEX,
];

// Create phone number mask. Format is "(555) 333-4444" or "+1 (555) 333-4444"
export const createPhoneNumberMask = () => (value: string) => {
  // remove any non-number characters to simplify length checking
  value = (value || '').replace(/\D/g, '');

  const mask = [
    '(',
    CharacterMaskRegex.DIGIT_REGEX,
    CharacterMaskRegex.DIGIT_REGEX,
    CharacterMaskRegex.DIGIT_REGEX,
    ')',
    ' ',
    CharacterMaskRegex.DIGIT_REGEX,
    CharacterMaskRegex.DIGIT_REGEX,
    CharacterMaskRegex.DIGIT_REGEX,
    '-',
    CharacterMaskRegex.DIGIT_REGEX,
    CharacterMaskRegex.DIGIT_REGEX,
    CharacterMaskRegex.DIGIT_REGEX,
    CharacterMaskRegex.DIGIT_REGEX,
  ];

  // If number is long enough and starts with 1, use mask with country code
  if (value.length > 10 && value[0] === '1') {
    return ['+', '1', ' ', ...mask];
  }

  return mask;
};

/**
 * Generate a mask on each render based on the current input value
 * @param value current value of the input
 * @param languageId language to format with
 *
 * @output EN 999 || 1,000 || 10,000 || 100,000
 * @output FR 999 || 1 000 || 10 000 || 100 000
 */
export const createFormattedNumberMask = (value: string, languageId: string): TextInputMask => {
  // Remove spaces and commas
  const unformattedValue = value ? value.replace(new RegExp(/,|\s/, 'g'), '') : '';

  // Format into english readable string
  const humanized = intComma(parseInt(unformattedValue, 10));
  // Transform string into an array
  let mask: TextInputMask = humanized.split('');

  // Create mask
  mask = mask.map((maskValue: string) => {
    // If character is a comma, change it for a space (if french)
    if (maskValue === ',') return languageId === 'fr' ? ' ' : maskValue;
    // Otherwise, accept only integer
    return CharacterMaskRegex.DIGIT_REGEX;
  });

  return mask;
};

// Transform value to a title case format
export const createTitleCaseTransformation = (value: string): string => formatTitleCase(value);

// Transform value to uppercase
export const createUpperCaseTransformation = (value: string): string => {
  if (isNil(value)) return value;
  return value.toUpperCase();
};
